import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ChevronsUpDown, Check, Loader2, Plus, Lock, Building2 } from "lucide-react";
import { useEntitlements } from "@/lib/useEntitlements";
import { GateNeutralButton } from "@/components/UpgradeLock";

/**
 * Header BRAND label + switcher — its own control, beside the workspace one.
 *
 * WHY IT IS SEPARATE (owner decision, 20 Jul 2026). It began as a section inside
 * the workspace menu, on the argument that a brand only exists inside a
 * workspace and two header controls would invite the exact confusion this area
 * is about — changing brand while believing you changed workspace. That argument
 * is not wrong, and it is recorded here rather than deleted.
 *
 * What outweighed it: brand is what you actually work in. Workspace is changed
 * rarely, brand constantly, and burying the frequent action inside the rare one
 * made the frequent action feel like a setting. The nesting also produced a menu
 * with two independent scroll regions (measured: caps at 320px and 256px), which
 * at 15 brands on a paid plan is a menu where the wheel scrolls whichever region
 * the cursor happens to sit over.
 *
 * The mitigation for the confusion risk is visual, not structural: this control
 * uses a different icon and a "Brand" caption, so the two are told apart by what
 * they SAY rather than by where they sit.
 *
 * SHOWN WHENEVER A BRAND EXISTS, including at one. The earlier in-menu version
 * hid itself below two brands, which was right for a nested section — a switcher
 * that cannot switch is noise. It is wrong for a standalone control, because the
 * only route to a SECOND brand is the "Add brand" action inside this menu. Hiding
 * it at one brand would make multi-brand unreachable.
 */
export default function BrandSwitcher() {
  const [open, setOpen] = useState(false);
  const [switchingBrand, setSwitchingBrand] = useState(null);
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const navigate = useNavigate();
  const { brand, brands, brandLoading, switchBrand } = useAuth();

  // Three states, never two. `canCreateBrand` fails closed, so before
  // entitlements land it is false — which is why the render branches on
  // pending/unknown FIRST. Reading the boolean alone would show a paying
  // customer a locked "Add brand" on every page load.
  const {
    loaded: entLoaded,
    unknown: entUnknown,
    canCreateBrand,
    brandsAllowed,
  } = useEntitlements();

  // Portaled to document.body with a full-screen backdrop, so it escapes the
  // sticky header's stacking context and a click anywhere outside closes it.
  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + 8, left: r.left });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const onMove = () => place();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  const switchToBrand = useCallback(
    async (b) => {
      if (b.brand_id === brand?.brand_id || switchingBrand) return;
      setSwitchingBrand(b.brand_id);
      try {
        // Deliberately NOT a navigate: you are already looking at a
        // brand-scoped screen and every brand-scoped page is meaningful for
        // every brand. `switchBrand` clears, bumps and re-derives in one commit,
        // so no frame shows the previous brand's numbers under the new name.
        await switchBrand(b.brand_id);
        setOpen(false);
      } catch (e) {
        console.error("[brands/switch] failed", e?.response?.status);
        // COPY SLOT brand.switch.error
        toast.error("Couldn't switch brand. Try again.");
      } finally {
        setSwitchingBrand(null);
      }
    },
    [brand, switchingBrand, switchBrand]
  );

  // Nothing to label and nothing to add into — the workspace has no brand yet,
  // which is the pre-onboarding state. Onboarding is the route in, not this.
  if (!brandLoading && brands.length === 0) return null;

  // Fail closed through the transition: mid-switch `brand` is null by design
  // (clear → bump → re-derive), so show a neutral skeleton rather than the
  // brand being left behind.
  const name = brand?.name || "";
  const loadingLabel = brandLoading || !name;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid="brand-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-lm-border bg-lm-surface hover:bg-lm-tint/40 transition-colors max-w-[15rem]"
      >
        <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-lm-tint2 text-lm-primary">
          <Building2 className="w-3.5 h-3.5" />
        </span>
        <span className="min-w-0 text-left">
          {/* The caption is the mitigation for sitting beside the workspace
              control: the two are told apart by what they say. */}
          <span className="block text-[10px] font-mono uppercase tracking-widest text-lm-text3 leading-none">
            Brand
          </span>
          {loadingLabel ? (
            <span
              data-brand-slot="loading"
              className="block mt-1 h-3 w-24 rounded bg-lm-tint animate-pulse"
            />
          ) : (
            <span
              data-testid="brand-switcher-current"
              data-brand-slot="name"
              className="block text-sm font-medium text-lm-text truncate leading-tight mt-0.5"
            >
              {name}
            </span>
          )}
        </span>
        <ChevronsUpDown className="w-3.5 h-3.5 text-lm-text3 shrink-0" />
      </button>

      {open && coords
        ? createPortal(
            <>
              <div className="fixed inset-0 z-[99]" onClick={() => setOpen(false)} />
              <div
                style={{ top: coords.top, left: coords.left }}
                className="fixed z-[100] w-72 max-w-[calc(100vw-1rem)] bg-lm-surface border border-lm-border rounded-xl shadow-[0_16px_40px_-12px_rgba(0,0,0,0.28)] overflow-hidden animate-[fadeIn_0.15s_ease]"
              >
                {/* ONE scroll region, not two. The nested version had a
                    separate cap per section, so the wheel scrolled whichever
                    the cursor sat over. */}
                <ul className="max-h-80 overflow-y-auto p-1.5">
                  {brands.map((b) => {
                    const active = b.brand_id === brand?.brand_id;
                    return (
                      <li key={b.brand_id}>
                        <button
                          onClick={() => switchToBrand(b)}
                          disabled={switchingBrand !== null}
                          data-testid={`brand-switcher-item-${b.brand_id}`}
                          className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-left transition-colors disabled:opacity-70 ${
                            active ? "bg-lm-tint/70 ring-1 ring-lm-primary/20" : "hover:bg-lm-tint/40"
                          }`}
                        >
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                              active ? "bg-lm-primary text-white" : "bg-lm-tint2 text-lm-primary"
                            }`}
                          >
                            <Building2 className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate text-lm-text">{b.name}</div>
                            {/* An unconfirmed brand is a half-finished draft that
                                `list[0]` can still seat as "the brand". Saying so
                                is the cheapest honest signal we have — the owner
                                can at least see WHY a scan looks thin. */}
                            {b.status !== "confirmed" ? (
                              <div className="text-[11px] text-lm-text3 mt-0.5">Not finished yet</div>
                            ) : null}
                          </div>
                          <div className="shrink-0">
                            {switchingBrand === b.brand_id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-lm-text3" />
                            ) : active ? (
                              <Check className="w-4 h-4 text-lm-primary" />
                            ) : null}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {/* THREE STATES, and the at-cap one stays VISIBLE — an invisible
                    ceiling is worse than a refused click, because the owner
                    cannot see that a limit exists or what it is. */}
                <div className="border-t border-lm-border p-1.5">
                  {!entLoaded ? (
                    <div className="px-1 py-1">
                      <GateNeutralButton
                        unknown={entUnknown}
                        size="md"
                        className="w-full justify-center"
                        data-testid="brand-switcher-add-pending"
                      />
                    </div>
                  ) : canCreateBrand ? (
                    <button
                      type="button"
                      data-testid="brand-switcher-add"
                      onClick={() => {
                        setOpen(false);
                        // `?new=1` tells Onboarding this is an ADDITIONAL brand:
                        // bypass the "you already have a brand" redirect, and
                        // re-ask category and market instead of inheriting brand
                        // #1's. See Onboarding.jsx.
                        navigate("/onboarding?new=1");
                      }}
                      disabled={switchingBrand !== null}
                      className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-left transition-colors hover:bg-lm-tint/40 disabled:opacity-70"
                    >
                      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-lm-tint2 text-lm-primary">
                        <Plus className="w-4 h-4" />
                      </span>
                      {/* COPY SLOT brand.create.action */}
                      <span className="text-sm font-medium text-lm-text">Add brand</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="brand-switcher-add-locked"
                      onClick={() => {
                        setOpen(false);
                        navigate("/app/billing");
                      }}
                      className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-left transition-colors hover:bg-amber-50"
                    >
                      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-amber-50 border border-amber-200 text-amber-700">
                        <Lock className="w-4 h-4" strokeWidth={2.2} />
                      </span>
                      <span className="min-w-0">
                        {/* COPY SLOT brand.create.atcap — ORI, PLEASE REVIEW.
                            States the real number from entitlements rather than
                            a vague "limit reached". Never says "unlimited". */}
                        <span className="block text-sm font-medium text-lm-text">Add brand</span>
                        <span className="block text-[11px] text-lm-text3 mt-0.5">
                          Your plan includes {brandsAllowed}
                          {brandsAllowed === 1 ? " brand" : " brands"} — upgrade for more
                        </span>
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </>,
            document.body
          )
        : null}
    </>
  );
}
